import { useEffect, useMemo, useState } from 'react'
import { ErrorApi } from '../api/cliente'
import { calcularEfectivo, REGLAS_POR_DEFECTO, type ReglasEfectivo } from '../api/efectivo'
import { imprimirComprobante } from '../api/impresion'
import { dinero } from '../api/formato'
import { clientes as apiClientes, configuracion as apiConfiguracion, ventas as apiVentas } from '../api/servicios'
import type { Cliente, Comprobante, MedioPago, ResultadoCobro, Venta } from '../api/tipos'
import { Dialogo } from './Dialogo'
import { Ticket } from './Ticket'

interface Props {
    venta: Venta
    alCerrar: () => void
    alCobrar: () => Promise<void> | void
}

export function DialogoCobro({ venta, alCerrar, alCobrar }: Props) {
    const [reglas, setReglas] = useState<ReglasEfectivo>(REGLAS_POR_DEFECTO)
    const [medios, setMedios] = useState<MedioPago[]>([])

    // Los medios que el cajero eligio, en el orden en que los eligio. El
    // efectivo no esta aca: es siempre lo que falta para llegar al total.
    const [elegidos, setElegidos] = useState<number[]>([])
    const [montos, setMontos] = useState<Record<number, string>>({})
    const [referencias, setReferencias] = useState<Record<number, string>>({})

    // Fiado: lo que queda anotado en la cuenta del cliente. No es una tarjeta
    // de credito; antes compartia el rotulo "credito" con ella y se confundian.
    const [fiado, setFiado] = useState('')
    const [recibido, setRecibido] = useState('')

    const [cliente, setCliente] = useState<Cliente | null>(null)
    const [buscandoCliente, setBuscandoCliente] = useState('')
    const [candidatos, setCandidatos] = useState<Cliente[]>([])

    const [comprobante, setComprobante] = useState<Comprobante | null>(null)

    /**
     * Se avisa solo cuando el ticket NO salio por la impresora termica. En el
     * caso normal no se dice nada: el cajero ya tiene el papel en la mano.
     */
    const [avisoImpresion, setAvisoImpresion] = useState<string | null>(null)

    const [error, setError] = useState<string | null>(null)
    const [cobrando, setCobrando] = useState(false)
    const [resultado, setResultado] = useState<ResultadoCobro | null>(null)

    // Las reglas del efectivo las define la configuracion del negocio: el
    // multiplo que circula y si el redondeo puede subir. Las tres viajan juntas
    // porque por separado se contradicen.
    useEffect(() => {
        void apiConfiguracion
            .obtener()
            .then((datos) =>
                setReglas({
                    decimales: datos.decimalesMoneda,
                    multiplo: datos.redondeoEfectivo,
                    haciaAbajo: datos.redondeoHaciaAbajo,
                }),
            )
            .catch(() => setReglas(REGLAS_POR_DEFECTO))
    }, [])

    // Los medios los da la API con su identificador real. Tenerlos escritos en
    // el navegador con los identificadores asumidos registra los cobros contra
    // el medio equivocado si la base se sembro en otro orden, y sin dar error.
    useEffect(() => {
        void apiVentas
            .mediosPago()
            .then(setMedios)
            .catch(() => setMedios([]))
    }, [])

    useEffect(() => {
        const consulta = buscandoCliente.trim()

        if (consulta.length < 2) {
            setCandidatos([])
            return
        }

        const control = new AbortController()
        const temporizador = window.setTimeout(async () => {
            try {
                const pagina = await apiClientes.buscar(consulta, control.signal)
                setCandidatos(pagina.items)
            } catch {
                /* la busqueda de cliente no debe romper el cobro */
            }
        }, 220)

        return () => {
            window.clearTimeout(temporizador)
            control.abort()
        }
    }, [buscandoCliente])

    const numero = (texto: string) => {
        const valor = Number.parseFloat(texto.replace(',', '.'))
        return Number.isFinite(valor) && valor > 0 ? Math.round(valor) : 0
    }

    const efectivo = medios.find((m) => m.afectaEfectivo) ?? null
    const otros = medios.filter((m) => !m.afectaEfectivo)

    const montoDe = (medioPagoId: number) => numero(montos[medioPagoId] ?? '')
    const totalOtros = elegidos.reduce((suma, id) => suma + montoDe(id), 0)
    const montoFiado = numero(fiado)

    const calculo = useMemo(
        () => calcularEfectivo(venta.total, totalOtros, montoFiado, reglas),
        [venta.total, totalOtros, montoFiado, reglas],
    )

    /**
     * Elegir un medio le pone de entrada lo que falta para el total, que es el
     * caso normal: el cliente paga todo con una tarjeta. Si es un pago mixto,
     * el cajero corrige el importe y el resto queda en efectivo solo.
     */
    function alternarMedio(medio: MedioPago) {
        const yaEsta = elegidos.includes(medio.medioPagoId)

        if (yaEsta) {
            setElegidos((actuales) => actuales.filter((id) => id !== medio.medioPagoId))
            setMontos((actuales) => ({ ...actuales, [medio.medioPagoId]: '' }))
            setReferencias((actuales) => ({ ...actuales, [medio.medioPagoId]: '' }))
            return
        }

        const falta = venta.total - totalOtros - montoFiado

        setElegidos((actuales) => [...actuales, medio.medioPagoId])
        setMontos((actuales) => ({
            ...actuales,
            [medio.medioPagoId]: falta > 0 ? String(falta) : '',
        }))
    }

    const montoRecibido = numero(recibido)
    const vuelto = montoRecibido > 0 ? montoRecibido - calculo.requerido : 0

    const clienteAsignado = cliente ?? (venta.clienteId ? { clienteId: venta.clienteId } as Cliente : null)

    const problemas: string[] = []

    if (calculo.restante < 0) {
        problemas.push(`Los pagos superan el total en ${dinero(-calculo.restante)}.`)
    }

    for (const id of elegidos) {
        const medio = otros.find((m) => m.medioPagoId === id)
        if (!medio) continue

        // Un medio elegido y dejado en blanco es el error peligroso: el cajero
        // marco "Debito", no puso el importe, y la venta entera se cobraria en
        // efectivo sin que nada avise. Cuadraria mal recien en el cierre.
        if (montoDe(id) <= 0) {
            problemas.push(`Elegiste ${medio.nombre} pero no pusiste el importe.`)
            continue
        }

        if (medio.requiereReferencia && (referencias[id] ?? '').trim() === '') {
            problemas.push(`Un pago con ${medio.nombre.toLowerCase()} exige el número de operación.`)
        }
    }

    if (montoFiado > 0 && !clienteAsignado) {
        problemas.push('Para dejar saldo fiado hay que identificar al cliente.')
    }
    if (montoRecibido > 0 && montoRecibido < calculo.requerido) {
        problemas.push('El efectivo recibido no alcanza.')
    }

    const puedeCobrar = problemas.length === 0 && !cobrando

    async function cobrar() {
        setError(null)
        setCobrando(true)

        try {
            if (cliente && cliente.clienteId !== venta.clienteId) {
                await apiVentas.asignarCliente(venta.ventaId, cliente.clienteId)
            }

            const pagos: { medioPagoId: number; importe: number; referencia?: string }[] = []

            if (calculo.requerido > 0 && efectivo) {
                pagos.push({ medioPagoId: efectivo.medioPagoId, importe: calculo.requerido })
            }

            for (const id of elegidos) {
                const importe = montoDe(id)
                if (importe <= 0) continue

                const referencia = (referencias[id] ?? '').trim()

                pagos.push({
                    medioPagoId: id,
                    importe,
                    referencia: referencia === '' ? undefined : referencia,
                })
            }

            const cobro = await apiVentas.completar(venta.ventaId, {
                pagos,
                saldoACredito: montoFiado,
                montoRecibidoEfectivo: montoRecibido > 0 ? montoRecibido : undefined,
            })

            setResultado(cobro)
        } catch (fallo) {
            setError(fallo instanceof ErrorApi ? fallo.mensajeVisible() : 'No se pudo completar el cobro.')
        } finally {
            setCobrando(false)
        }
    }

    async function imprimir() {
        setAvisoImpresion(null)

        // Todavia no llego el comprobante del servidor: se imprime la vista que
        // ya esta en pantalla, que es lo unico que hay.
        if (!comprobante) {
            window.print()
            return
        }

        const salida = await imprimirComprobante(comprobante)

        if (salida.via === 'navegador') {
            setAvisoImpresion(salida.motivo)
        }
    }

    // --- Venta cobrada: comprobante y vuelto -------------------------------

    if (resultado) {
        return (
            <Dialogo
                titulo={`Venta ${resultado.venta.numero}`}
                alCerrar={() => void alCobrar()}
                acciones={
                    <>
                        <button
                            type="button"
                            className="boton"
                            onClick={() => void imprimir()}
                        >
                            Imprimir
                        </button>
                        <button
                            type="button"
                            className="boton boton-primario boton-grande"
                            onClick={() => void alCobrar()}
                            autoFocus
                        >
                            Siguiente venta
                        </button>
                    </>
                }
            >
                <div className="columna g4">
                    {resultado.vuelto > 0 && (
                        <div className="panel columna g1 centro" style={{ textAlign: 'center' }}>
                            <span className="tenue">Vuelto</span>
                            <strong className="numero" style={{ fontSize: '2.5rem', color: 'var(--exito)' }}>
                                {dinero(resultado.vuelto)}
                            </strong>
                        </div>
                    )}

                    <div className="columna g2">
                        <div className="fila entre">
                            <span className="tenue">Total</span>
                            <span className="numero">{dinero(resultado.venta.total)}</span>
                        </div>

                        {resultado.redondeo !== 0 && (
                            <div className="fila entre chico tenue">
                                <span>Redondeo del efectivo</span>
                                <span className="numero">
                                    {resultado.redondeo > 0 ? '+' : ''}
                                    {dinero(resultado.redondeo)}
                                </span>
                            </div>
                        )}

                        {resultado.saldoACredito > 0 && (
                            <div className="fila entre" style={{ color: 'var(--alerta)' }}>
                                <span>Queda a crédito</span>
                                <span className="numero">{dinero(resultado.saldoACredito)}</span>
                            </div>
                        )}
                    </div>

                    {avisoImpresion && (
                        <p className="aviso">
                            Se imprimió desde el navegador: {avisoImpresion}
                        </p>
                    )}

                    <Ticket ventaId={resultado.venta.ventaId} alCargar={setComprobante} />
                </div>
            </Dialogo>
        )
    }

    // --- Formulario de cobro ------------------------------------------------

    return (
        <Dialogo
            titulo="Cobrar"
            alCerrar={alCerrar}
            acciones={
                <>
                    <button type="button" className="boton" onClick={alCerrar} disabled={cobrando}>
                        Volver
                    </button>
                    <button
                        type="button"
                        className="boton boton-primario boton-grande"
                        onClick={() => void cobrar()}
                        disabled={!puedeCobrar}
                    >
                        {cobrando ? <span className="cargando" /> : `Cobrar ${dinero(venta.total)}`}
                    </button>
                </>
            }
        >
            <div className="columna g4">
                <div className="panel fila entre" style={{ padding: 'var(--e3)' }}>
                    <span className="tenue">Total a pagar</span>
                    <strong className="numero" style={{ fontSize: '1.5rem' }}>
                        {dinero(venta.total)}
                    </strong>
                </div>

                {error && (
                    <p className="aviso aviso-error" role="alert">
                        {error}
                    </p>
                )}

                {/* Efectivo: lo calcula el sistema, el cajero no lo teclea. */}
                <div className="panel columna g2" style={{ padding: 'var(--e3)' }}>
                    <div className="fila entre">
                        <strong>Efectivo</strong>
                        <strong className="numero" style={{ fontSize: '1.25rem' }}>
                            {dinero(calculo.requerido)}
                        </strong>
                    </div>

                    {calculo.redondeo !== 0 && (
                        <p className="diminuto tenue">
                            Redondeado {calculo.redondeo > 0 ? 'hacia arriba' : 'hacia abajo'} en{' '}
                            {dinero(Math.abs(calculo.redondeo))}: no circulan monedas de 1 ni 5 dinero.
                        </p>
                    )}

                    {calculo.requerido > 0 && (
                        <div>
                            <label className="etiqueta" htmlFor="recibido">Con cuánto paga</label>
                            <input
                                id="recibido"
                                className="campo campo-numero"
                                value={recibido}
                                onChange={(evento) => setRecibido(evento.target.value)}
                                inputMode="numeric"
                                autoComplete="off"
                                placeholder="Opcional"
                            />
                            {vuelto > 0 && (
                                <p className="fila entre chico" style={{ marginTop: 'var(--e2)' }}>
                                    <span className="tenue">Vuelto</span>
                                    <strong className="numero" style={{ color: 'var(--exito)' }}>
                                        {dinero(vuelto)}
                                    </strong>
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Eleccion del medio, a la vista. Antes vivia dentro de un
                    bloque plegado y habia que saber que estaba ahi. */}
                <div className="columna g3">
                    <span className="etiqueta">¿Con qué paga?</span>

                    <div className="medios-pago">
                        {otros.map((medio) => (
                            <button
                                key={medio.medioPagoId}
                                type="button"
                                className={`medio-pago${
                                    elegidos.includes(medio.medioPagoId) ? ' medio-pago-activo' : ''
                                }`}
                                aria-pressed={elegidos.includes(medio.medioPagoId)}
                                onClick={() => alternarMedio(medio)}
                            >
                                {medio.nombre}
                            </button>
                        ))}
                    </div>

                    {elegidos.map((id) => {
                        const medio = otros.find((m) => m.medioPagoId === id)
                        if (!medio) return null

                        return (
                            <div key={id} className="medio-pago-linea">
                                <div>
                                    <label className="etiqueta" htmlFor={`monto-${id}`}>
                                        {medio.nombre}
                                    </label>
                                    <input
                                        id={`monto-${id}`}
                                        className="campo campo-numero"
                                        value={montos[id] ?? ''}
                                        onChange={(evento) =>
                                            setMontos((actuales) => ({
                                                ...actuales,
                                                [id]: evento.target.value,
                                            }))
                                        }
                                        inputMode="numeric"
                                        autoComplete="off"
                                    />
                                </div>

                                {medio.requiereReferencia && (
                                    <div>
                                        <label className="etiqueta" htmlFor={`referencia-${id}`}>
                                            Número de operación
                                        </label>
                                        <input
                                            id={`referencia-${id}`}
                                            className="campo"
                                            value={referencias[id] ?? ''}
                                            onChange={(evento) =>
                                                setReferencias((actuales) => ({
                                                    ...actuales,
                                                    [id]: evento.target.value,
                                                }))
                                            }
                                            autoComplete="off"
                                        />
                                        <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                                            El que imprime el voucher. Es lo que permite encontrar
                                            esta venta si al cierre el total no cuadra.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                <details className="panel" style={{ padding: 'var(--e3)' }}>
                    <summary style={{ cursor: 'pointer', minHeight: 'var(--toque)', display: 'flex', alignItems: 'center' }}>
                        Dejar fiado
                    </summary>

                    <div className="columna g3" style={{ marginTop: 'var(--e3)' }}>
                        <div>
                            <label className="etiqueta" htmlFor="fiado">
                                Queda anotado en la cuenta del cliente
                            </label>
                            <input
                                id="fiado"
                                className="campo campo-numero"
                                value={fiado}
                                onChange={(evento) => setFiado(evento.target.value)}
                                inputMode="numeric"
                                autoComplete="off"
                            />
                            <p className="tenue diminuto" style={{ marginTop: 'var(--e1)' }}>
                                No es la tarjeta de crédito: es deuda con el local, y se cobra
                                después.
                            </p>
                        </div>

                        {montoFiado > 0 && (
                            <div>
                                <label className="etiqueta" htmlFor="cliente">Cliente</label>

                                {cliente ? (
                                    <div className="fila entre panel" style={{ padding: 'var(--e2) var(--e3)' }}>
                                        <span className="truncar">
                                            {cliente.nombreCompleto}
                                            {cliente.saldoCredito > 0 && (
                                                <span className="tenue diminuto">
                                                    {' '}· debe {dinero(cliente.saldoCredito)}
                                                </span>
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            className="boton boton-fantasma"
                                            onClick={() => setCliente(null)}
                                            aria-label="Quitar cliente"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            id="cliente"
                                            className="campo"
                                            value={buscandoCliente}
                                            onChange={(evento) => setBuscandoCliente(evento.target.value)}
                                            placeholder="Nombre o RUT"
                                            autoComplete="off"
                                        />
                                        {candidatos.length > 0 && (
                                            <ul className="buscador-opciones">
                                                {candidatos.map((candidato) => (
                                                    <li key={candidato.clienteId}>
                                                        <button
                                                            type="button"
                                                            className="buscador-opcion"
                                                            onClick={() => {
                                                                setCliente(candidato)
                                                                setBuscandoCliente('')
                                                                setCandidatos([])
                                                            }}
                                                        >
                                                            <span className="truncar">{candidato.nombreCompleto}</span>
                                                            <span className="numero tenue diminuto">
                                                                {candidato.saldoCredito > 0
                                                                    ? `debe ${dinero(candidato.saldoCredito)}`
                                                                    : 'sin deuda'}
                                                            </span>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </details>

                {problemas.length > 0 && (
                    <ul className="aviso aviso-atencion columna g1" style={{ listStyle: 'none', margin: 0 }}>
                        {problemas.map((problema) => (
                            <li key={problema}>{problema}</li>
                        ))}
                    </ul>
                )}
            </div>
        </Dialogo>
    )
}
